import { useCallback, useEffect, useMemo, useState } from "react";
import {
  DEFAULT_MATERIAL,
  DEFAULT_SIZE,
  formatFtIn,
  getComponentSpec,
  getMaterial,
  getReducer,
  getSizeSpec,
  getSupportSpec,
  getTerminationSpec,
  maxSpanFt,
  nearestSize,
  type ComponentKind,
  type MaterialId,
  type SupportKind,
  type TerminationKind,
} from "@/lib/pipe-spec";

/** A point in 3D pipe space, in INCHES. x = east/right, y = north/left, z = up. */
export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export type AxisId = "x+" | "x-" | "y+" | "y-" | "z+" | "z-";

export const AXES: Record<AxisId, Vec3> = {
  "x+": { x: 1, y: 0, z: 0 },
  "x-": { x: -1, y: 0, z: 0 },
  "y+": { x: 0, y: 1, z: 0 },
  "y-": { x: 0, y: -1, z: 0 },
  "z+": { x: 0, y: 0, z: 1 },
  "z-": { x: 0, y: 0, z: -1 },
};

/** A named fabrication spool — a group of runs drawn and shipped as one piece. */
export interface Spool {
  id: string;
  /** short mark, e.g. "SP-01" */
  mark: string;
  name: string;
}

/** A continuous pipe run: a polyline of vertices. Turns become fittings. */
export interface PipeRun {
  id: string;
  material: MaterialId;
  /** size of the first segment (kept for labelling) */
  size: string;
  pts: Vec3[];
  /** size of each straight segment; length = pts.length - 1 */
  segSizes: string[];
  /** fabrication spool this run belongs to */
  spool?: string;
  /** set when this run tees off an existing run */
  branch?: { hostRunId: string; at: Vec3; hostDir?: Vec3 | null };
}


export type FittingKind = "elbow90" | "elbow45" | "tee" | "reducer";

export interface RunFitting {
  runId: string;
  /** index of the vertex in run.pts */
  index: number;
  at: Vec3;
  kind: FittingKind;
  /** turn angle for elbows */
  angle: number;
  size: string;
  /** second size for reducers */
  sizeOut?: string;
  label: string;
  welds: number;
  /** for tees: screen-independent direction of the branch take-off */
  branchDir?: Vec3;
}

/** An in-line valve / flange set placed on a straight segment. */
export interface SketchComponent {
  id: string;
  runId: string;
  /** segment index within the run */
  index: number;
  /** distance from the segment start, inches (centerline of the component) */
  station: number;
  kind: ComponentKind;
  size: string;
  tag?: string;
}

/** Free text note anchored in model space. */
export interface SketchNote {
  id: string;
  at: Vec3;
  text: string;
}

/** A placed component resolved into model-space geometry. */
export interface ComponentPlacement extends SketchComponent {
  center: Vec3;
  from: Vec3;
  to: Vec3;
  dir: Vec3;
  length: number;
  label: string;
  short: string;
  note: string;
  weight: number;
  joints: number;
}

/** A hanger / guide / anchor set on a straight segment. */
export interface SketchSupport {
  id: string;
  runId: string;
  index: number;
  /** distance from the segment start, inches */
  station: number;
  kind: SupportKind;
  size: string;
  tag?: string;
}

/** How a run terminates: cap, flange, or a piece of equipment. */
export interface SketchTermination {
  id: string;
  runId: string;
  /** which free end of the run it sits on */
  end: "start" | "end";
  kind: TerminationKind;
  size: string;
  tag?: string;
}

export interface SupportPlacement extends SketchSupport {
  at: Vec3;
  dir: Vec3;
  label: string;
  short: string;
  detail: string;
  rodSize: string;
  note: string;
  weight: number;
  /** stable mark, e.g. "H-01" */
  mark: string;
  /** true when the pipe here runs vertically */
  vertical: boolean;
}

export interface TerminationPlacement extends SketchTermination {
  at: Vec3;
  /** unit vector pointing OUT of the pipe at that end */
  dir: Vec3;
  label: string;
  short: string;
  detail: string;
  note: string;
  takeoff: number;
  joints: number;
  weight: number;
  equipment: boolean;
  /** stable mark, e.g. "E-01" */
  mark: string;
}

/** Spans that exceed the code spacing for their size. */
export interface SpanWarning {
  runId: string;
  index: number;
  size: string;
  spanFt: number;
  maxFt: number;
  needed: number;
}


export interface CutPiece {
  runId: string;
  index: number;
  /** sub-piece index when components split a segment */
  sub: number;
  /** stable mark number shown on the ISO and in the cut list, e.g. "P-01" */
  tag: string;
  size: string;
  /** center-to-center length of the whole segment, inches */
  centerLength: number;
  /** actual cut length after fitting / component takeoffs, inches */
  cutLength: number;
  from: Vec3;
  to: Vec3;
  /** takeoff removed at each end, inches (used by the renderer too) */
  trimStart: number;
  trimEnd: number;
  weldEnds: number;
}

export interface SketchDoc {
  version: 2;
  name: string;
  material: MaterialId;
  size: string;
  runs: PipeRun[];
  components: SketchComponent[];
  notes: SketchNote[];
  supports?: SketchSupport[];
  terminations?: SketchTermination[];
  /** fabrication spools */
  spools?: Spool[];
  /** elevation of the z = 0 plane, inches above datum */
  baseElevation?: number;
  /** user-dragged dimension line offsets, keyed `${runId}-${segIndex}` (inches, signed) */
  dimOffsets?: Record<string, number>;
}


export interface SavedSketch {
  id: string;
  name: string;
  savedAt: number;
  doc: SketchDoc;
}

export interface SketchTotals {
  totalFeet: number;
  totalWeight: number;
  supportWeight: number;
  bySize: [string, { feet: number; weight: number; pieces: number }][];
  fittingCounts: [string, number][];
  componentCounts: [string, number][];
  supportCounts: [string, number][];
  terminationCounts: [string, number][];
  welds: number;
  pieces: number;
}

/**
 * Roll up quantities for any subset of a sketch — the whole drawing or a single
 * spool. Pure and deterministic so every panel reads the same numbers.
 */
export function computeSketchTotals({
  material,
  cutList,
  fittings,
  placements,
  supportPlacements,
  terminationPlacements,
}: {
  material: MaterialId;
  cutList: CutPiece[];
  fittings: RunFitting[];
  placements: ComponentPlacement[];
  supportPlacements: SupportPlacement[];
  terminationPlacements: TerminationPlacement[];
}): SketchTotals {
  const bySize = new Map<string, { feet: number; weight: number; pieces: number }>();
  let weight = 0;
  for (const p of cutList) {
    const spec = getSizeSpec(material, p.size);
    const ft = p.cutLength / 12;
    const w = ft * spec.lbPerFt;
    weight += w;
    const cur = bySize.get(p.size) ?? { feet: 0, weight: 0, pieces: 0 };
    cur.feet += ft;
    cur.weight += w;
    cur.pieces += 1;
    bySize.set(p.size, cur);
  }
  const fittingCounts = new Map<string, number>();
  for (const f of fittings) {
    fittingCounts.set(f.label, (fittingCounts.get(f.label) ?? 0) + 1);
  }
  const componentCounts = new Map<string, number>();
  for (const p of placements) {
    componentCounts.set(p.note, (componentCounts.get(p.note) ?? 0) + 1);
    weight += p.weight;
  }
  const supportCounts = new Map<string, number>();
  let supportWeight = 0;
  for (const s of supportPlacements) {
    supportCounts.set(s.note, (supportCounts.get(s.note) ?? 0) + 1);
    supportWeight += s.weight;
  }
  const terminationCounts = new Map<string, number>();
  for (const t of terminationPlacements) {
    terminationCounts.set(t.note, (terminationCounts.get(t.note) ?? 0) + 1);
    weight += t.weight;
  }
  const welds =
    fittings.reduce((s, f) => s + f.welds, 0) +
    placements.reduce((s, p) => s + p.joints, 0) +
    terminationPlacements.reduce((s, t) => s + t.joints, 0);
  return {
    totalFeet: cutList.reduce((s, p) => s + p.cutLength / 12, 0),
    totalWeight: weight,
    supportWeight,
    bySize: [...bySize.entries()],
    fittingCounts: [...fittingCounts.entries()],
    componentCounts: [...componentCounts.entries()],
    supportCounts: [...supportCounts.entries()],
    terminationCounts: [...terminationCounts.entries()],
    welds,
    pieces: cutList.length,
  };
}


const STORE_KEY = "tt-pipe-sketch-saves";
const AUTOSAVE_KEY = "tt-pipe-sketch-autosave";

const eq = (a: Vec3, b: Vec3) => a.x === b.x && a.y === b.y && a.z === b.z;
const sub = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
const add = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x + b.x, y: a.y + b.y, z: a.z + b.z });
const mul = (v: Vec3, s: number): Vec3 => ({ x: v.x * s, y: v.y * s, z: v.z * s });
const len = (v: Vec3) => Math.hypot(v.x, v.y, v.z);
const norm = (v: Vec3): Vec3 => {
  const l = len(v) || 1;
  return { x: v.x / l, y: v.y / l, z: v.z / l };
};
const dot = (a: Vec3, b: Vec3) => a.x * b.x + a.y * b.y + a.z * b.z;

function readSaves(): SavedSketch[] {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    const list = raw ? (JSON.parse(raw) as SavedSketch[]) : [];
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

export function useSketch() {
  const [material, setMaterialState] = useState<MaterialId>(DEFAULT_MATERIAL);
  const [size, setSize] = useState<string>(DEFAULT_SIZE);
  const [runs, setRuns] = useState<PipeRun[]>([]);
  const [components, setComponents] = useState<SketchComponent[]>([]);
  const [notes, setNotes] = useState<SketchNote[]>([]);
  const [supports, setSupports] = useState<SketchSupport[]>([]);
  const [terminations, setTerminations] = useState<SketchTermination[]>([]);
  const [spools, setSpools] = useState<Spool[]>([]);
  /** spool new runs get assigned to */
  const [activeSpool, setActiveSpool] = useState<string | null>(null);
  /** elevation of the z = 0 plane, inches above datum */
  const [baseElevation, setBaseElevation] = useState(0);
  /** user-dragged dimension line offsets, keyed `${runId}-${segIndex}` */
  const [dimOffsets, setDimOffsets] = useState<Record<string, number>>({});
  const [name, setName] = useState("Untitled sketch");
  const [history, setHistory] = useState<PipeRun[][]>([]);
  const [future, setFuture] = useState<PipeRun[][]>([]);
  const [saves, setSaves] = useState<SavedSketch[]>([]);
  /** Run currently being drawn (id) */
  const [activeRunId, setActiveRunId] = useState<string | null>(null);

  useEffect(() => {
    setSaves(readSaves());
    try {
      const raw = localStorage.getItem(AUTOSAVE_KEY);
      if (raw) {
        const doc = JSON.parse(raw) as SketchDoc;
        if (doc?.runs?.length) {
          setRuns(doc.runs);
          setComponents(doc.components ?? []);
          setNotes(doc.notes ?? []);
          setSupports(doc.supports ?? []);
          setTerminations(doc.terminations ?? []);
          setSpools(doc.spools ?? []);
          setBaseElevation(doc.baseElevation ?? 0);
          setDimOffsets(doc.dimOffsets ?? {});

          setMaterialState(doc.material ?? DEFAULT_MATERIAL);
          setSize(doc.size ?? DEFAULT_SIZE);
          setName(doc.name ?? "Untitled sketch");
        }
      }
    } catch {
      /* ignore corrupt autosave */
    }
  }, []);

  /** Keep the size valid when the material changes (not every material has 10"). */
  const setMaterial = useCallback((m: MaterialId) => {
    setMaterialState(m);
    setSize((s) => nearestSize(m, s));
  }, []);

  const commit = useCallback((next: PipeRun[]) => {
    setHistory((h) => [...h.slice(-49), runs]);
    setFuture([]);
    setRuns(next);
  }, [runs]);

  const activeRun = useMemo(
    () => runs.find((r) => r.id === activeRunId) ?? null,
    [runs, activeRunId],
  );

  const newId = (p = "run") => `${p}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

  /** Start a new run at a point (or restart drawing). */
  const startRun = useCallback((p: Vec3) => {
    const id = newId();
    commit([...runs, { id, material, size, pts: [p], segSizes: [], spool: activeSpool ?? undefined }]);
    setActiveRunId(id);
  }, [commit, runs, material, size, activeSpool]);


  /**
   * Start a run that tees off an existing run at `p`.
   * The host segment is split so the tee lands on a real vertex.
   */
  const branchFrom = useCallback((hostRunId: string, p: Vec3) => {
    const host = runs.find((r) => r.id === hostRunId);
    if (!host) return;
    let nextHost = host;
    let hostDir: Vec3 | null = null;
    let splitAt = -1;

    const vertexIndex = host.pts.findIndex((q) => eq(q, p));
    if (vertexIndex >= 0) {
      // locking onto an existing vertex: only interior vertices can carry a tee
      if (vertexIndex === 0 || vertexIndex === host.pts.length - 1) return;
      hostDir = norm(sub(host.pts[vertexIndex], host.pts[vertexIndex - 1]));
    } else {
      // find the segment containing p and split it
      for (let i = 0; i < host.pts.length - 1; i++) {
        const a = host.pts[i];
        const b = host.pts[i + 1];
        const ab = sub(b, a);
        const ap = sub(p, a);
        const t = dot(ap, ab) / (dot(ab, ab) || 1);
        if (t <= 0.001 || t >= 0.999) continue;
        const proj = add(a, mul(ab, t));
        if (len(sub(proj, p)) < 0.6) {
          splitAt = i;
          break;
        }
      }
      if (splitAt < 0) return;
      hostDir = norm(sub(host.pts[splitAt + 1], host.pts[splitAt]));
      nextHost = {
        ...host,
        pts: [...host.pts.slice(0, splitAt + 1), p, ...host.pts.slice(splitAt + 1)],
        segSizes: [
          ...host.segSizes.slice(0, splitAt + 1),
          host.segSizes[splitAt],
          ...host.segSizes.slice(splitAt + 1),
        ],
      };
    }

    // components living on the split segment have to follow the split
    if (splitAt >= 0) {
      const a = host.pts[splitAt];
      const cutAt = len(sub(p, a));
      setComponents((cs) =>
        cs.map((c) => {
          if (c.runId !== hostRunId) return c;
          if (c.index < splitAt) return c;
          if (c.index > splitAt) return { ...c, index: c.index + 1 };
          return c.station <= cutAt
            ? c
            : { ...c, index: c.index + 1, station: c.station - cutAt };
        }),
      );
    }

    const id = newId();
    commit([
      ...runs.map((r) => (r.id === hostRunId ? nextHost : r)),
      {
        id,
        material,
        size,
        pts: [p],
        segSizes: [],
        spool: activeSpool ?? host.spool,
        branch: { hostRunId, at: p, hostDir },
      },
    ]);
    setActiveRunId(id);
  }, [runs, commit, material, size, activeSpool]);



  /** Add the next vertex to the active run, using the currently selected size. */
  const extendRun = useCallback((p: Vec3) => {
    if (!activeRunId) return;
    const next = runs.map((r) => {
      if (r.id !== activeRunId) return r;
      const last = r.pts[r.pts.length - 1];
      if (eq(last, p)) return r;
      return { ...r, pts: [...r.pts, p], segSizes: [...r.segSizes, size] };
    });
    commit(next);
  }, [activeRunId, runs, commit, size]);

  /** Finish the run in progress. Drops single-point runs. */
  const finishRun = useCallback(() => {
    setRuns((rs) => rs.filter((r) => r.pts.length > 1));
    setActiveRunId(null);
  }, []);

  const removeLastVertex = useCallback(() => {
    if (!activeRunId) return;
    const next = runs
      .map((r) =>
        r.id === activeRunId
          ? { ...r, pts: r.pts.slice(0, -1), segSizes: r.segSizes.slice(0, -1) }
          : r,
      )
      .filter((r) => r.pts.length > 0);
    setRuns(next);
    if (!next.find((r) => r.id === activeRunId)) setActiveRunId(null);
  }, [activeRunId, runs]);

  const deleteRun = useCallback((id: string) => {
    commit(runs.filter((r) => r.id !== id && r.branch?.hostRunId !== id));
    setComponents((cs) => cs.filter((c) => c.runId !== id));
    setSupports((ss) => ss.filter((s) => s.runId !== id));
    setTerminations((ts) => ts.filter((t) => t.runId !== id));
    if (activeRunId === id) setActiveRunId(null);
  }, [commit, runs, activeRunId]);

  const clearAll = useCallback(() => {
    commit([]);
    setComponents([]);
    setNotes([]);
    setSupports([]);
    setTerminations([]);
    setSpools([]);
    setActiveSpool(null);
    setDimOffsets({});
    setActiveRunId(null);
  }, [commit]);

  /* ---------------- fabrication spools ---------------- */

  /** Create a new spool and make it the target for new runs. */
  const addSpool = useCallback((spoolName?: string) => {
    const id = `sp-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`;
    setSpools((list) => {
      const mark = `SP-${String(list.length + 1).padStart(2, "0")}`;
      return [...list, { id, mark, name: spoolName?.trim() || mark }];
    });
    setActiveSpool(id);
    return id;
  }, []);

  const renameSpool = useCallback((id: string, patch: Partial<Pick<Spool, "mark" | "name">>) => {
    setSpools((list) => list.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  }, []);

  /** Delete a spool; its runs fall back to unassigned. */
  const removeSpool = useCallback((id: string) => {
    setSpools((list) => list.filter((s) => s.id !== id));
    setRuns((rs) => rs.map((r) => (r.spool === id ? { ...r, spool: undefined } : r)));
    setActiveSpool((cur) => (cur === id ? null : cur));
  }, []);

  /** Move a run into a spool (or out of one with `null`). */
  const assignRunToSpool = useCallback((runId: string, spoolId: string | null) => {
    setRuns((rs) => rs.map((r) => (r.id === runId ? { ...r, spool: spoolId ?? undefined } : r)));
  }, []);


  /* ---------------- dimension editing ---------------- */

  /** Move a dimension line off its pipe. `offset` is signed inches along the line normal. */
  const setDimOffset = useCallback((dimKey: string, offset: number) => {
    setDimOffsets((d) => ({ ...d, [dimKey]: offset }));
  }, []);

  /** Put a dragged dimension line back on its automatic side. */
  const resetDimOffset = useCallback((dimKey: string) => {
    setDimOffsets((d) => {
      if (!(dimKey in d)) return d;
      const next = { ...d };
      delete next[dimKey];
      return next;
    });
  }, []);

  /**
   * Re-length a straight from its dimension text. The far end of the segment and
   * everything downstream of it (including branch runs hung off moved vertices)
   * translates along the segment axis, so the spool stays connected and every
   * derived fitting / cut length is recomputed.
   */
  const setSegmentLength = useCallback(
    (runId: string, index: number, inches: number) => {
      const run = runs.find((r) => r.id === runId);
      if (!run) return;
      const a = run.pts[index];
      const b = run.pts[index + 1];
      if (!a || !b) return;
      const current = len(sub(b, a));
      // keep at least 1", snap to 1/16"
      const target = Math.max(1, Math.round(inches * 16) / 16);
      const delta = target - current;
      if (!Number.isFinite(delta) || Math.abs(delta) < 0.01) return;
      const shift = mul(norm(sub(b, a)), delta);

      // branch runs anchored to a vertex that moves must travel with it
      const carried = new Set<string>();
      const collect = (hostId: string, movedPts: Vec3[]) => {
        const moved = new Set(movedPts.map(key));
        for (const r of runs) {
          if (r.branch?.hostRunId !== hostId || carried.has(r.id)) continue;
          if (!moved.has(key(r.branch.at))) continue;
          carried.add(r.id);
          collect(r.id, r.pts);
        }
      };
      collect(runId, run.pts.slice(index + 1));

      commit(
        runs.map((r) => {
          if (r.id === runId) {
            return { ...r, pts: r.pts.map((p, i) => (i > index ? add(p, shift) : p)) };
          }
          if (carried.has(r.id)) {
            return {
              ...r,
              pts: r.pts.map((p) => add(p, shift)),
              branch: r.branch ? { ...r.branch, at: add(r.branch.at, shift) } : r.branch,
            };
          }
          return r;
        }),
      );

      // keep in-line components inside the re-lengthed straight
      setComponents((cs) =>
        cs.map((c) =>
          c.runId === runId && c.index === index
            ? { ...c, station: Math.max(0, Math.min(target, c.station)) }
            : c,
        ),
      );
    },
    [runs, commit],
  );


  const undo = useCallback(() => {
    setHistory((h) => {
      if (!h.length) return h;
      const prev = h[h.length - 1];
      setFuture((f) => [runs, ...f]);
      setRuns(prev);
      setActiveRunId(null);
      return h.slice(0, -1);
    });
  }, [runs]);

  const redo = useCallback(() => {
    setFuture((f) => {
      if (!f.length) return f;
      setHistory((h) => [...h, runs]);
      setRuns(f[0]);
      return f.slice(1);
    });
  }, [runs]);

  /* ---------------- components + notes ---------------- */

  const addComponent = useCallback(
    (c: Omit<SketchComponent, "id">) => {
      setComponents((cs) => [...cs, { ...c, id: newId("cmp") }]);
    },
    [],
  );

  const removeComponent = useCallback((id: string) => {
    setComponents((cs) => cs.filter((c) => c.id !== id));
  }, []);

  const tagComponent = useCallback((id: string, tag: string) => {
    setComponents((cs) => cs.map((c) => (c.id === id ? { ...c, tag } : c)));
  }, []);

  const addNote = useCallback((at: Vec3, text: string) => {
    if (!text.trim()) return;
    setNotes((ns) => [...ns, { id: newId("note"), at, text: text.trim() }]);
  }, []);

  const removeNote = useCallback((id: string) => {
    setNotes((ns) => ns.filter((n) => n.id !== id));
  }, []);

  /* ---------------- supports & terminations ---------------- */

  const addSupport = useCallback((s: Omit<SketchSupport, "id">) => {
    setSupports((ss) => [...ss, { ...s, id: newId("sup") }]);
  }, []);

  const removeSupport = useCallback((id: string) => {
    setSupports((ss) => ss.filter((s) => s.id !== id));
  }, []);

  /** One termination per free end — placing a second one replaces the first. */
  const addTermination = useCallback((t: Omit<SketchTermination, "id">) => {
    setTerminations((ts) => [
      ...ts.filter((x) => !(x.runId === t.runId && x.end === t.end)),
      { ...t, id: newId("term") },
    ]);
  }, []);

  const removeTermination = useCallback((id: string) => {
    setTerminations((ts) => ts.filter((t) => t.id !== id));
  }, []);


  /**
   * Fittings implied by the geometry: direction changes -> elbows,
   * size changes -> concentric reducers, branch starts -> tees.
   * Also produces the takeoff consumed on each side of every vertex.
   */
  const derived = useMemo(() => {
    const fittings: RunFitting[] = [];
    /** runId -> per-vertex takeoff on the incoming / outgoing side */
    const trims = new Map<string, { in: number[]; out: number[] }>();

    for (const run of runs) {
      trims.set(run.id, {
        in: new Array(run.pts.length).fill(0),
        out: new Array(run.pts.length).fill(0),
      });
    }

    // tee lookups: host vertex -> branch direction (must be square to the host run)
    const teeAt = new Map<string, Vec3>();
    for (const run of runs) {
      if (!run.branch || run.pts.length < 2) continue;
      const dir = norm(sub(run.pts[1], run.pts[0]));
      const hd = run.branch.hostDir;
      if (hd && Math.abs(dot(dir, hd)) > 0.01) continue; // not a 90° take-off
      teeAt.set(`${run.branch.hostRunId}|${key(run.branch.at)}`, dir);
    }


    for (const run of runs) {
      const t = trims.get(run.id)!;

      // branch start: this run leaves a tee on its host
      if (run.branch && run.segSizes.length) {
        const bs = getSizeSpec(run.material, run.segSizes[0]);
        t.out[0] += bs.teeBranch;
      }

      for (let i = 1; i < run.pts.length - 1; i++) {
        const sizeIn = run.segSizes[i - 1];
        const sizeOut = run.segSizes[i];
        const specIn = getSizeSpec(run.material, sizeIn);
        const din = norm(sub(run.pts[i], run.pts[i - 1]));
        const dout = norm(sub(run.pts[i + 1], run.pts[i]));
        const cos = Math.max(-1, Math.min(1, dot(din, dout)));
        const turn = Math.round((Math.acos(cos) * 180) / Math.PI);

        const tee = teeAt.get(`${run.id}|${key(run.pts[i])}`);
        let placed = false;

        if (turn >= 1) {
          const is45 = turn <= 46;
          const takeoff = is45 ? specIn.elbow45 : specIn.elbow90;
          t.in[i] += takeoff;
          t.out[i] += takeoff;
          const mat = getMaterial(run.material);
          fittings.push({
            runId: run.id,
            index: i,
            at: run.pts[i],
            kind: is45 ? "elbow45" : "elbow90",
            angle: is45 ? 45 : 90,
            size: sizeIn,
            label: `${sizeIn} ${is45 ? 45 : 90}° Elbow ${
              mat.jointType === "bw" ? "LR BW" : mat.jointType === "solder" ? "Solder" : "Socket"
            }`,
            welds: 2,
          });
          placed = true;
        }

        if (tee) {
          t.in[i] += specIn.teeRun;
          t.out[i] += specIn.teeRun;
          fittings.push({
            runId: run.id,
            index: i,
            at: run.pts[i],
            kind: "tee",
            angle: 90,
            size: sizeIn,
            label: `${sizeIn} Straight Tee`,
            welds: 3,
            branchDir: tee,
          });
          placed = true;
        }

        if (sizeIn !== sizeOut) {
          const red = getReducer(run.material, sizeIn, sizeOut);
          if (red) {
            // a reducer straddles the vertex unless a fitting is already there,
            // in which case it sits downstream of that fitting
            if (placed) {
              t.out[i] += red.length;
            } else {
              t.in[i] += red.length / 2;
              t.out[i] += red.length / 2;
            }
            fittings.push({
              runId: run.id,
              index: i,
              at: run.pts[i],
              kind: "reducer",
              angle: 0,
              size: sizeIn,
              sizeOut,
              label: red.label,
              welds: 2,
            });
          }
        }
      }
    }

    // caps, flanges and equipment nozzles eat length off the free ends
    for (const term of terminations) {
      const run = runs.find((r) => r.id === term.runId);
      const t = run ? trims.get(run.id) : null;
      if (!run || !t || run.pts.length < 2) continue;
      const last = run.pts.length - 1;
      const nps = term.end === "start" ? run.segSizes[0] : run.segSizes[last - 1];
      const spec = getTerminationSpec(run.material, term.kind, nps ?? run.size);
      if (term.end === "start") t.out[0] += spec.takeoff;
      else t.in[last] += spec.takeoff;
    }

    return { fittings, trims };
  }, [runs, terminations]);

  const fittings = derived.fittings;
  const segTrims = derived.trims;

  /** Components resolved onto their segment geometry. */
  const placements = useMemo<ComponentPlacement[]>(() => {
    const out: ComponentPlacement[] = [];
    for (const c of components) {
      const run = runs.find((r) => r.id === c.runId);
      if (!run || c.index >= run.pts.length - 1) continue;
      const a = run.pts[c.index];
      const b = run.pts[c.index + 1];
      const total = len(sub(b, a));
      const dir = norm(sub(b, a));
      const spec = getComponentSpec(run.material, c.kind, c.size);
      const station = Math.max(0, Math.min(total, c.station));
      const center = add(a, mul(dir, station));
      out.push({
        ...c,
        center,
        dir,
        from: add(center, mul(dir, -spec.length / 2)),
        to: add(center, mul(dir, spec.length / 2)),
        length: spec.length,
        label: spec.label,
        short: spec.short,
        note: spec.note,
        weight: spec.weight,
        joints: spec.joints,
      });
    }
    return out;
  }, [components, runs]);

  /** Supports resolved onto their segment geometry, numbered H-01, H-02 … */
  const supportPlacements = useMemo<SupportPlacement[]>(() => {
    const out: SupportPlacement[] = [];
    const ordered = [...supports].sort((a, b) =>
      a.runId === b.runId ? a.index - b.index || a.station - b.station : a.runId < b.runId ? -1 : 1,
    );
    for (const s of ordered) {
      const run = runs.find((r) => r.id === s.runId);
      if (!run || s.index >= run.pts.length - 1) continue;
      const a = run.pts[s.index];
      const b = run.pts[s.index + 1];
      const total = len(sub(b, a));
      const dir = norm(sub(b, a));
      const spec = getSupportSpec(run.material, s.kind, s.size);
      const at = add(a, mul(dir, Math.max(0, Math.min(total, s.station))));
      out.push({
        ...s,
        at,
        dir,
        label: spec.label,
        short: spec.short,
        detail: spec.detail,
        rodSize: spec.rodSize,
        note: spec.note,
        weight: spec.weight,
        mark: `H-${String(out.length + 1).padStart(2, "0")}`,
        vertical: Math.abs(dir.z) > 0.9,
      });
    }
    return out;
  }, [supports, runs]);

  /** Terminations resolved to the free end they sit on, numbered E-01, E-02 … */
  const terminationPlacements = useMemo<TerminationPlacement[]>(() => {
    const out: TerminationPlacement[] = [];
    for (const t of terminations) {
      const run = runs.find((r) => r.id === t.runId);
      if (!run || run.pts.length < 2) continue;
      const last = run.pts.length - 1;
      const at = t.end === "start" ? run.pts[0] : run.pts[last];
      const inner = t.end === "start" ? run.pts[1] : run.pts[last - 1];
      const dir = norm(sub(at, inner));
      const nps = t.end === "start" ? run.segSizes[0] : run.segSizes[last - 1];
      const spec = getTerminationSpec(run.material, t.kind, nps ?? run.size);
      out.push({
        ...t,
        at,
        dir,
        label: spec.label,
        short: spec.short,
        detail: spec.detail,
        note: spec.note,
        takeoff: spec.takeoff,
        joints: spec.joints,
        weight: spec.weight,
        equipment: spec.equipment,
        mark: `E-${String(out.length + 1).padStart(2, "0")}`,
      });
    }
    return out;
  }, [terminations, runs]);

  /**
   * Horizontal straights carrying fewer supports than the code spacing allows.
   * Vertical legs are excluded — those get riser clamps at the floor instead.
   */
  const spanWarnings = useMemo<SpanWarning[]>(() => {
    const out: SpanWarning[] = [];
    for (const run of runs) {
      for (let i = 0; i < run.pts.length - 1; i++) {
        const a = run.pts[i];
        const b = run.pts[i + 1];
        const d = sub(b, a);
        if (Math.abs(norm(d).z) > 0.9) continue;
        const spanFt = len(d) / 12;
        const nps = run.segSizes[i] ?? run.size;
        const maxFt = maxSpanFt(run.material, nps);
        const have = supports.filter((s) => s.runId === run.id && s.index === i).length;
        const needed = Math.max(0, Math.ceil(spanFt / maxFt) - 1);
        if (have >= needed || needed === 0) continue;
        out.push({ runId: run.id, index: i, size: nps, spanFt, maxFt, needed: needed - have });
      }
    }
    return out;
  }, [runs, supports]);


  /** Straight pipe pieces with fitting + component takeoffs removed. */
  const cutList = useMemo<CutPiece[]>(() => {
    const out: CutPiece[] = [];
    for (const run of runs) {
      const t = segTrims.get(run.id);
      if (!t) continue;
      for (let i = 0; i < run.pts.length - 1; i++) {
        const a = run.pts[i];
        const b = run.pts[i + 1];
        const centerLength = len(sub(b, a));
        if (centerLength === 0) continue;
        const dir = norm(sub(b, a));
        const trimStart = t.out[i] ?? 0;
        const trimEnd = t.in[i + 1] ?? 0;

        // components on this segment break the straight into several cuts
        const onSeg = placements
          .filter((p) => p.runId === run.id && p.index === i)
          .sort((p, q) => p.station - q.station);

        let cursor = trimStart;
        let startTrim = trimStart;
        let subIdx = 0;
        const pieces: Array<{ from: number; to: number; ts: number; te: number }> = [];
        for (const p of onSeg) {
          const cs = p.station - p.length / 2;
          const ce = p.station + p.length / 2;
          pieces.push({ from: cursor, to: Math.max(cursor, cs), ts: startTrim, te: p.length / 2 });
          cursor = ce;
          startTrim = p.length / 2;
        }
        pieces.push({
          from: cursor,
          to: centerLength - trimEnd,
          ts: startTrim,
          te: trimEnd,
        });

        for (const pc of pieces) {
          const cut = pc.to - pc.from;
          if (cut <= 0.01) continue;
          out.push({
            runId: run.id,
            index: i,
            sub: subIdx++,
            tag: `P-${String(out.length + 1).padStart(2, "0")}`,
            size: run.segSizes[i] ?? run.size,
            centerLength,
            cutLength: cut,
            from: add(a, mul(dir, pc.from)),
            to: add(a, mul(dir, pc.to)),
            trimStart: pc.ts,
            trimEnd: pc.te,
            weldEnds: (pc.ts > 0 ? 1 : 0) + (pc.te > 0 ? 1 : 0),
          });
        }
      }
    }
    return out;
  }, [runs, segTrims, placements]);

  const totals = useMemo(
    () =>
      computeSketchTotals({
        material,
        cutList,
        fittings,
        placements,
        supportPlacements,
        terminationPlacements,
      }),
    [cutList, fittings, material, placements, supportPlacements, terminationPlacements],
  );


  /* ---------------- save / load ---------------- */

  const doc = useMemo<SketchDoc>(
    () => ({
      version: 2,
      name,
      material,
      size,
      runs,
      components,
      notes,
      supports,
      terminations,
      spools,
      baseElevation,
      dimOffsets,
    }),
    [name, material, size, runs, components, notes, supports, terminations, spools, baseElevation, dimOffsets],
  );

  // autosave so a closed tab / offline reload never loses work
  useEffect(() => {
    try {
      localStorage.setItem(AUTOSAVE_KEY, JSON.stringify(doc));
    } catch {
      /* storage full — ignore */
    }
  }, [doc]);

  const persist = useCallback((list: SavedSketch[]) => {
    setSaves(list);
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify(list));
    } catch {
      /* ignore */
    }
  }, []);

  const saveSketch = useCallback(
    (saveName?: string) => {
      const finalName = (saveName ?? name).trim() || "Untitled sketch";
      setName(finalName);
      const list = readSaves();
      const existing = list.find((s) => s.name === finalName);
      const entry: SavedSketch = {
        id: existing?.id ?? newId("sk"),
        name: finalName,
        savedAt: Date.now(),
        doc: { ...doc, name: finalName },
      };
      const next = existing
        ? list.map((s) => (s.id === existing.id ? entry : s))
        : [entry, ...list];
      persist(next);
      return entry;
    },
    [doc, name, persist],
  );

  const loadSketch = useCallback((id: string) => {
    const entry = readSaves().find((s) => s.id === id);
    if (!entry) return;
    setRuns(entry.doc.runs ?? []);
    setComponents(entry.doc.components ?? []);
    setNotes(entry.doc.notes ?? []);
    setSupports(entry.doc.supports ?? []);
    setTerminations(entry.doc.terminations ?? []);
    setSpools(entry.doc.spools ?? []);
    setActiveSpool(null);
    setBaseElevation(entry.doc.baseElevation ?? 0);
    setDimOffsets(entry.doc.dimOffsets ?? {});
    setMaterialState(entry.doc.material ?? DEFAULT_MATERIAL);
    setSize(entry.doc.size ?? DEFAULT_SIZE);
    setName(entry.doc.name ?? "Untitled sketch");
    setActiveRunId(null);
    setHistory([]);
    setFuture([]);
  }, []);

  const deleteSave = useCallback(
    (id: string) => persist(readSaves().filter((s) => s.id !== id)),
    [persist],
  );

  const newSketch = useCallback(() => {
    setRuns([]);
    setComponents([]);
    setNotes([]);
    setSupports([]);
    setTerminations([]);
    setSpools([]);
    setActiveSpool(null);
    setDimOffsets({});
    setActiveRunId(null);
    setHistory([]);
    setFuture([]);
    setName("Untitled sketch");
  }, []);

  return {
    material,
    setMaterial,
    size,
    setSize,
    name,
    setName,
    runs,
    components,
    placements,
    notes,
    activeRun,
    activeRunId,
    startRun,
    branchFrom,
    extendRun,
    finishRun,
    removeLastVertex,
    deleteRun,
    clearAll,
    addComponent,
    removeComponent,
    tagComponent,
    addNote,
    removeNote,
    supports,
    supportPlacements,
    addSupport,
    removeSupport,
    terminations,
    terminationPlacements,
    addTermination,
    removeTermination,
    spanWarnings,
    spools,
    activeSpool,
    setActiveSpool,
    addSpool,
    renameSpool,
    removeSpool,
    assignRunToSpool,
    baseElevation,
    setBaseElevation,
    dimOffsets,
    setDimOffset,
    resetDimOffset,
    setSegmentLength,
    undo,
    redo,
    canUndo: history.length > 0,
    canRedo: future.length > 0,
    fittings,
    segTrims,
    cutList,
    totals,
    formatFtIn,
    saves,
    saveSketch,
    loadSketch,
    deleteSave,
    newSketch,
  };
}

function key(p: Vec3) {
  return `${p.x},${p.y},${p.z}`;
}
